import {inject, Injectable, signal} from "@angular/core"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {CachedEvaluator} from "@cm/graph-v3"
import {DataResolver, MaterialNode, ThreeContext, ThreeEvaluator} from "@cm/material-nodes"
import {HashedMaterialNodeWithColorOutput} from "@cm/template-nodes"
import {TypedImageData} from "@cm/utils"
import {Three as THREE} from "@cm/material-nodes/three"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {DEFAULT_FLOAT_TEXTURE_TYPE} from "@app/template-editor/helpers/three-utils"
// WebGPU requires 256-byte row alignment for buffer reads.
// Bytes per pixel depends on texture type: Float32 RGBA = 16, HalfFloat RGBA = 8.
const WEBGPU_BUFFER_ALIGNMENT = 256
const BYTES_PER_CHANNEL = {[THREE.FloatType]: 4, [THREE.HalfFloatType]: 2} as const

@Injectable()
export class BakeTextureService {
    readonly threeSceneManagerService = signal<ThreeSceneManagerService | undefined>(undefined)
    readonly logger = inject(CM_LOGGER_TEXTURES)

    async bakeTexture(
        textureNode: HashedMaterialNodeWithColorOutput,
        widthUV: number,
        heightUV: number,
        widthPx: number,
        heightPx: number,
        resolver: DataResolver,
    ): Promise<TypedImageData> {
        const threeSceneManagerService = this.threeSceneManagerService()
        if (!threeSceneManagerService) throw new Error("ThreeSceneManagerService not available")
        const threeRenderer = await threeSceneManagerService.getRenderer()

        const threeDefaultFloatTextureType = DEFAULT_FLOAT_TEXTURE_TYPE
        const bytesPerPixel = BYTES_PER_CHANNEL[threeDefaultFloatTextureType] * 4 // 4 channels (RGBA)

        if (!(textureNode.node.source instanceof MaterialNode)) {
            throw new Error("Expected material node")
        }

        const webGPUMode = threeSceneManagerService.isWebGPUMode()

        // Align width to WebGPU buffer requirements (256-byte rows)
        const webGPUWidthAlignment = WEBGPU_BUFFER_ALIGNMENT / bytesPerPixel
        const alignedWidthPx = webGPUMode ? Math.ceil(widthPx / webGPUWidthAlignment) * webGPUWidthAlignment : widthPx

        const bakedMaterial = new THREE.MeshBasicNodeMaterial()

        const {materialManagerService} = threeSceneManagerService

        const {context, referencedTextureNodes, referencedTiledTextureInstances} = materialManagerService.createThreeContext({
            textureResolution: "original",
            textureStreamingMode: "off",
            threeRenderer,
            progressiveTextureLoading: false,
            resolve: resolver,
            logger: this.logger,
        })

        const getFilter = () => {
            if (context.forceFiltering === "nearest") return THREE.NearestFilter
            else return THREE.LinearFilter
        }

        const evaluator = new CachedEvaluator<ThreeContext>(context)
        const result = await textureNode.node.source.toThree(evaluator as ThreeEvaluator)
        const colorSlot = textureNode.node.parameters.key
        bakedMaterial.colorNode = (result[colorSlot] ?? null) as THREE.Node<"color"> | null

        const renderTarget = new THREE.RenderTarget(alignedWidthPx, heightPx, {
            type: threeDefaultFloatTextureType,
            minFilter: getFilter(),
            magFilter: getFilter(),
            format: THREE.RGBAFormat,
            colorSpace: THREE.NoColorSpace,
        })

        const renderScene = new THREE.Scene()

        // In WebGPU mode, we render to the full aligned width and scale the camera frustum proportionally.
        // This avoids viewport/render-target mismatch issues that cause stretching in WebGPU.
        const alignmentRatio = alignedWidthPx / widthPx
        const cameraWidthUV = widthUV * alignmentRatio

        const planeMesh = (() => {
            const plane = new THREE.Mesh(new THREE.PlaneGeometry(widthUV, heightUV), bakedMaterial)
            const uvAttribute = plane.geometry.attributes.uv.array
            // Plane geometry comes with 1x1 UV, with origin in bottom-left corner
            // Rescale UVs, and flip Y only for WebGL (framebuffer origin is bottom-left)
            // WebGPU framebuffer origin is already top-left, so no flip needed
            const flipY = !webGPUMode
            for (let i = 0; i < uvAttribute.length; i += 2) {
                uvAttribute[i] *= widthUV
                uvAttribute[i + 1] = (flipY ? 1 - uvAttribute[i + 1] : uvAttribute[i + 1]) * heightUV
            }
            plane.geometry.attributes.uv.needsUpdate = true
            return plane
        })()

        renderScene.add(planeMesh)

        // Camera frustum: in WebGPU mode, use asymmetric frustum to keep plane left-aligned
        // Left edge stays at -widthUV/2 (plane's left edge), right edge extends for alignment padding
        const cameraLeft = -widthUV / 2
        const cameraRight = cameraLeft + cameraWidthUV
        const renderCamera = new THREE.OrthographicCamera(cameraLeft, cameraRight, heightUV / 2, -heightUV / 2, 0.1, 2.0)

        renderCamera.position.z = 1.0

        const rendererState = THREE.RendererUtils.saveRendererState(threeRenderer)

        threeRenderer.setRenderTarget(renderTarget)
        threeRenderer.clear()
        threeRenderer.render(renderScene, renderCamera)

        THREE.RendererUtils.restoreRendererState(threeRenderer, rendererState)

        planeMesh.geometry.dispose()
        bakedMaterial.dispose()

        const alignedBuffer = await threeRenderer.readRenderTargetPixelsAsync(renderTarget, 0, 0, alignedWidthPx, heightPx)
        renderTarget.dispose()

        for (const textureNode of referencedTextureNodes) {
            materialManagerService.releaseTextureResource(textureNode)
        }
        for (const tiledTextureInstance of referencedTiledTextureInstances) tiledTextureInstance.release()

        // Convert half-float (Uint16Array) to Float32Array if needed — downstream consumers expect float32
        const alignedFloat32 =
            alignedBuffer instanceof Float32Array
                ? alignedBuffer
                : (() => {
                      const src = alignedBuffer as Uint16Array
                      const dst = new Float32Array(src.length)
                      for (let i = 0; i < src.length; i++) dst[i] = THREE.DataUtils.fromHalfFloat(src[i])
                      return dst
                  })()

        // Extract only the desired pixels if width was padded for alignment
        let bakedTextureBuffer: Float32Array
        if (alignedWidthPx !== widthPx) {
            bakedTextureBuffer = new Float32Array(widthPx * heightPx * 4)
            const alignedRowLength = alignedWidthPx * 4
            const targetRowLength = widthPx * 4
            for (let y = 0; y < heightPx; y++) {
                const srcOffset = y * alignedRowLength
                const dstOffset = y * targetRowLength
                bakedTextureBuffer.set(alignedFloat32.subarray(srcOffset, srcOffset + targetRowLength), dstOffset)
            }
        } else {
            bakedTextureBuffer = alignedFloat32
        }

        return {
            data: bakedTextureBuffer,
            width: widthPx,
            height: heightPx,
            channelLayout: "RGBA",
            dataType: "float32",
            colorSpace: "linear",
        }
    }
}
